import _ from 'underscore';

import { isDefined } from '../../../../../../utils/variables';
import { DesignNode } from '../../../../../elements/design/design-node/design-node';
import { reverseSegmentCurve } from '../../../../../elements/design/design-node/utils/conic';
import {
  LineSegmentId,
  lineSegmentIsBreakLine,
  LineSegmentNode,
} from '../../../../../elements/design/design-node/utils/line-segment-node';
import {
  getPointNeighbours,
  PointId,
  PointNode,
} from '../../../../../elements/design/design-node/utils/point-node';
import { findOuterPolygonsAndLines, getUsedSegments } from './find-outer-polygons-and-lines';
import { getPointIdArray } from './select';

type FindLineOrPolygonResult = {
  lines: LineSegmentNode[];
  isPolygon: boolean;
};

/**
 * Normalizes the order of the indices in the "values" property for each segment.
 * Example:
 * [1, 0], [1, 2], [2, 3] -> [0, 1], [1, 2], [2, 3]
 *
 * Returns copies of the segments, since changing the direction of a line segment in the design
 * would change its curve. The curve of a copy is reversed with its direction.
 */
function normalizeIndexOrder(input: LineSegmentNode[]) {
  const segments = input.map((x) => ({ ...x, values: [...x.values] as [PointId, PointId] }));

  if (segments.length === 1) {
    return segments;
  }

  for (let i = 0; i < segments.length; i++) {
    const values = segments[i].values;

    // For last index, check against previous, otherwise check towards next.
    const { neighbour, checkedValue } =
      i === segments.length - 1
        ? {
            neighbour: segments[i - 1],
            checkedValue: values[1],
          }
        : {
            neighbour: segments[i + 1],
            checkedValue: values[0],
          };

    if (neighbour.values.includes(checkedValue)) {
      values.reverse();
      const curve = segments[i].curve;
      segments[i].curve = curve && reverseSegmentCurve(curve);
    }
  }

  return segments;
}

/**
 * Identifies all the polygons and lines from the selection and returns them.
 */
export function listLinesAndPolygonsBySelection(
  designNode: DesignNode,
  selection: LineSegmentId[],
): FindLineOrPolygonResult[] {
  if (selection.length === 0) {
    return [];
  }
  const lineSegments = selection.map((id) => designNode.getLineSegmentById(id));
  const items: { lines: LineSegmentNode[]; isPolygon: boolean }[] = [];

  const result = findOuterPolygonsAndLines(designNode, lineSegments);

  const mapResult = (array: LineSegmentNode[][], isPolygon: boolean) => {
    return array.map((x) => {
      return {
        lines: normalizeIndexOrder(x),
        isPolygon,
      };
    });
  };

  items.push(...mapResult(result.polygons, true));
  items.push(
    ...mapResult(
      result.lines.map((x) => getUsedSegments(x, lineSegments, false)),
      false,
    ),
  );
  items.sort((a, b) => (a.lines.length < b.lines.length ? 1 : -1));

  return items;
}

/**
 * Will find the smallest polygon searching from the start line id.
 * If no polygon can be found it will instead return the longest line.
 * @param lineIndex Which index to start the search from.
 * @param filter If provided, the selection will be limited to these geometries.
 */
export function findSmallestPolygonOrLongestPolyline(
  node: DesignNode,
  startLineSegmentId: LineSegmentId,
  filter?: LineSegmentId[],
): FindLineOrPolygonResult {
  const startLine = node.getLineSegmentById(startLineSegmentId);
  const startNode = node.getPointById(startLine.values[0]);

  const lineSegmentFilter = (lineSegmentNode: LineSegmentNode) =>
    !lineSegmentIsBreakLine(node, lineSegmentNode.id);

  // If the start line is a breakline, return none.
  if (lineSegmentIsBreakLine(node, startLineSegmentId)) {
    return {
      lines: [],
      isPolygon: false,
    };
  }

  const lineSegmentNodes: LineSegmentNode[] = (filter ?? node.lineSegments)
    .map((x) => node.getLineSegmentById(x))
    .filter(lineSegmentFilter);

  const pointIds = getPointIdArray(lineSegmentNodes);
  const includedNodes = new Map<PointId, boolean>();

  pointIds.forEach((x) => includedNodes.set(x, true));

  const edges = new Map<PointId, { id: PointId; depth: number }>();

  const queue: {
    previous?: { node: PointNode; depth: number };
    current: { node: PointNode; depth: number };
  }[] = [{ current: { node: startNode, depth: 0 } }];

  const getPath = (pointId: PointId): LineSegmentNode[] => {
    const path: LineSegmentNode[] = [];

    let current: PointId = pointId;
    let next: PointId | undefined;
    do {
      next = edges.get(current)?.id;
      if (isDefined(next)) {
        const lineSegmentNode = node.findLineSegmentByPointIds(current, next);

        if (!isDefined(lineSegmentNode)) {
          throw new Error('Error getting path. Line segment node not found.');
        }

        path.push(lineSegmentNode);
        current = next;
      }
    } while (isDefined(next));

    return path;
  };

  while (queue.length > 0) {
    const { current, previous } = queue.shift()!;
    const neighbours = getPointNeighbours(current.node, node, {
      pointIdMap: includedNodes,
      lineSegmentFilter,
    });

    for (const neighbour of neighbours) {
      // Ignore if node is not included or if node is the previous node.
      if (!includedNodes.has(neighbour) || neighbour === previous?.node.id) {
        continue;
      }

      const currentLineSegment = node.findLineSegmentByPointIds(neighbour, current.node.id);
      const neighbourNode = node.getPointById(neighbour);

      if (!isDefined(currentLineSegment)) {
        throw new Error('Error finding smallest polygon or longest line. Missing nodes.');
      }

      if (edges.has(neighbour)) {
        const pathCurrent = getPath(current.node.id);
        const pathNeighbour = getPath(neighbour);

        // The result needs to contain the start line segment.
        if (
          !pathCurrent.map((x) => x.id).includes(startLineSegmentId) &&
          !pathNeighbour.map((x) => x.id).includes(startLineSegmentId)
        ) {
          continue;
        }

        // If the two branches share any points we discrard the branch.
        if (
          _.intersection(
            pathCurrent.map((x) => x.id),
            pathNeighbour.map((x) => x.id),
          ).length > 0
        ) {
          continue;
        }

        return {
          lines: [...pathCurrent.reverse(), ...pathNeighbour, currentLineSegment],
          isPolygon: true,
        };
      }

      edges.set(neighbour, { id: current.node.id, depth: current.depth + 1 });
      queue.push({ current: { node: neighbourNode, depth: current.depth + 1 }, previous: current });
    }
  }

  let result = [...edges].sort((a, b) => (a[1].depth > b[1].depth ? -1 : 1));

  const pathLeft = getPath(result[0][0]);
  const leftLineSegmentIds = pathLeft.map((x) => x.id);
  const leftPointIds = getPointIdArray(pathLeft);

  const rightPathShouldIncludeFirstSegment = !leftLineSegmentIds.includes(startLine.id);

  result = result.filter((x) => !leftPointIds.includes(x[0]));

  // Find a path to the right
  const pathRight = (() => {
    for (const item of result) {
      const nextPath = getPath(item[0]);

      if (
        rightPathShouldIncludeFirstSegment &&
        !nextPath.some((x) => x.id === startLineSegmentId)
      ) {
        continue;
      }

      if (nextPath.some((x) => leftLineSegmentIds.includes(x.id))) {
        continue;
      }
      return nextPath;
    }
    return [];
  })();

  return {
    isPolygon: false,
    lines: [...pathLeft, ...pathRight.reverse()],
  };
}
